import { BILLING_CURRENCIES, WORKSPACE_LANGUAGES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Select } from "@sagot/ui";
import { findCountry, useCountries } from "../lib/countries";
import { CountrySelect, TimezoneOptions } from "../lib/region-pickers";
import { asCurrency, regionDefaults, type WorkspaceRegion } from "./region-defaults";

interface RegionFieldsProps {
  value: WorkspaceRegion;
  onChange: (next: WorkspaceRegion) => void;
}

const COUNTRY_FIELD_ID = "onboarding-country";

/** Onboarding step 1: country, billing currency, timezone, language (country fills the rest). */
export function RegionFields({ value, onChange }: RegionFieldsProps) {
  const { t } = useTranslation();
  const countries = useCountries();
  return (
    <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="region-fields">
      <legend className="sr-only">{t("onboarding.org.regionLegend")}</legend>
      <Field label={t("onboarding.org.country")} id={COUNTRY_FIELD_ID}>
        <CountrySelect
          id={COUNTRY_FIELD_ID}
          value={value.country}
          onChange={(code) => {
            onChange(regionDefaults(code, value.language, findCountry(countries.data, code)));
          }}
        />
      </Field>
      <Field label={t("onboarding.org.currency")} hint={t("onboarding.org.currencyHint")}>
        <Select value={value.currency} onChange={(event) => { onChange({ ...value, currency: asCurrency(event.target.value) ?? value.currency }); }}>
          {BILLING_CURRENCIES.map((code) => (
            <option key={code} value={code}>
              {t(`settings.region.currencies.${code}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("onboarding.org.timezone")}>
        <Select value={value.timezone} onChange={(event) => { onChange({ ...value, timezone: event.target.value }); }}>
          <TimezoneOptions country={value.country} current={value.timezone} />
        </Select>
      </Field>
      <Field label={t("onboarding.org.language")}>
        <Select value={value.language} onChange={(event) => { onChange({ ...value, language: event.target.value === "fil" ? "fil" : "en" }); }}>
          {WORKSPACE_LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {t(`settings.org.languages.${lang}`)}
            </option>
          ))}
        </Select>
      </Field>
    </fieldset>
  );
}
